import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { profileAPI } from '../services/api';

const THEMES = {
  default: { name: 'Default', key: 'default' },
  lcars:   { name: 'LCARS',   key: 'lcars' },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('app_theme') || 'default';
  });

  // Apply theme to DOM + localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('app_theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    return () => document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  // Persist to backend (fire-and-forget) — auth is via HttpOnly cookies
  const persistToBackend = useCallback((newTheme) => {
    profileAPI.updateTheme(newTheme).catch(() => {
      // Silently ignore — localStorage is the fallback
    });
  }, []);

  // Called by profile load to seed theme from server
  const setThemeFromProfile = useCallback((serverTheme) => {
    if (serverTheme && THEMES[serverTheme]) {
      setThemeState(serverTheme);
    }
  }, []);

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    persistToBackend(newTheme);
  }, [persistToBackend]);

  const cycleTheme = useCallback(() => {
    const keys = Object.keys(THEMES);
    const idx = keys.indexOf(theme);
    const next = keys[(idx + 1) % keys.length];
    setTheme(next);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      setThemeFromProfile,
      cycleTheme,
      themes: THEMES,
      currentThemeName: THEMES[theme]?.name || theme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
